import { useApolloClient } from '@apollo/client';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import { InputBaseComponentProps } from '@material-ui/core/InputBase';
import Slider from '@material-ui/core/Slider';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import NumberFormat from 'react-number-format';
import { useDebounce } from 'react-use';
import EditIcon from '../assets/icon/edit.svg';
import {
  INITIAL_RANGE,
  SUM_ASSURED_DEFAULT,
  SUM_ASSURED_STEP
} from '../constants';
import { useQuotationContext } from '../contexts/QuotationContext';
import { GET_SUM_ASSURED_RANGE } from '../graphql';
import { ALERT_RED, ROSE, SILVER_CHALICE } from '../helpers/colors';

const formatter = new Intl.NumberFormat('zh-HK', {
  style: 'currency',
  currency: 'HKD',
  minimumFractionDigits: 0,
});

const NumberFormatCustom: React.FC<InputBaseComponentProps> = ({
  inputRef,
  onChange,
  ...props
}) => {
  const handleChange = useCallback(
    values => {
      onChange?.({
        target: {
          name: props.name,
          value: values.value,
        },
      } as unknown as React.SyntheticEvent<HTMLInputElement>);
    },
    [onChange, props.name],
  );
  return (
    // @ts-ignore
    <NumberFormat
      {...props}
      getInputRef={inputRef}
      onValueChange={handleChange}
      allowNegative={false}
      thousandSeparator
      prefix="$"
    />
  );
};

type SumAssuredProps = {
  value: number;
  age: number;
  handleChange: (arg1: any) => void;
};

const SumAssured = ({ value, age, handleChange }: SumAssuredProps) => {
  const client = useApolloClient();
  const { activeProduct, isSumAssuredValid, setIsSumAssuredValid } =
    useQuotationContext();
  const [range, setRange] = useState(INITIAL_RANGE);

  const [displayValue, setDisplayValue] = useState(value);

  useEffect(() => {
    const getRange = async () => {
      const { data } = await client.query({
        query: GET_SUM_ASSURED_RANGE,
        variables: { productId: activeProduct.code },
      });
      if (data && data.sumAssuredRange) {
        const activeRange = data.sumAssuredRange.find(
          level => level.max_issue_age >= age && level.min_issue_age <= age,
        );
        if (activeRange) {
          setRange({
            min: activeRange.min_sum_assured,
            max: activeRange.max_sum_assured,
          });
        }
      }
    };
    if (!isEmpty(activeProduct)) {
      getRange();
    }
  }, [activeProduct, age, client]);

  useDebounce(
    () => {
      handleChange({ target: { name: 'sumAssured', value: displayValue } });
    },
    500,
    [displayValue],
  );

  useEffect(() => {
    if (displayValue < range.min || displayValue > range.max) {
      setIsSumAssuredValid(false);
    } else {
      setIsSumAssuredValid(true);
    }
  }, [displayValue, range.max, range.min, setIsSumAssuredValid]);

  const handleBlur = useCallback(() => {
    if (displayValue < range.min || displayValue > range.max) {
      setDisplayValue(SUM_ASSURED_DEFAULT);
    }
  }, [displayValue, range.max, range.min]);

  const handleTextFieldChange = useCallback(
    event => setDisplayValue(Number(event.target.value)),
    [],
  );

  const handleSliderChange = useCallback((_event, newValue) => {
    if (typeof newValue === 'number') {
      setDisplayValue(newValue);
    }
  }, []);

  return (
    <>
      <Typography variant="body1">
        <FormattedMessage id="sumAssured.label" />
      </Typography>
      <Grid item xs={12} sm={6}>
        <TextField
          name="sumAssured"
          value={displayValue}
          onChange={handleTextFieldChange}
          onBlur={handleBlur}
          variant="outlined"
          InputProps={{
            inputComponent: NumberFormatCustom,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  disableRipple
                  style={{ backgroundColor: 'transparent', color: ROSE }}
                >
                  <EditIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          fullWidth
          size="small"
          style={{ paddingBottom: 18 }}
          helperText={
            !isSumAssuredValid ? (
              <span style={{ color: ALERT_RED }}>
                <FormattedMessage
                  id="invalid-sum-assured-range"
                  values={{
                    min: formatter.format(range.min),
                    max: formatter.format(range.max),
                  }}
                />
              </span>
            ) : (
              ''
            )
          }
        />
      </Grid>
      <Slider
        step={SUM_ASSURED_STEP}
        min={range.min}
        max={range.max}
        value={displayValue}
        onChange={handleSliderChange}
      />
      <Typography variant="subtitle1" style={{ color: SILVER_CHALICE }}>
        {`${formatter.format(range.min)}`}
        <span style={{ float: 'right' }}>
          {`${formatter.format(range.max)}`}
        </span>
      </Typography>
    </>
  );
};

export default SumAssured;
